/**
 * EmptyState — Crop Circle empty-state primitive.
 *
 * Renders a centered icon (optional), title, optional body message, and optional
 * action. All text uses Crop Circle tokens and styled Text — never bare strings
 * or react-native-paper Text (which defaults to Times New Roman on web).
 */
import React from 'react';
import { StyleSheet, Text, View, ViewStyle } from 'react-native';
import { colors, spacing, typography } from '../crop-circle/tokens';
import { Colors } from '../../themes/colors';

export interface EmptyStateProps {
  /** Primary empty-state message. Required. */
  title: string;
  /** Optional explanatory body text. */
  message?: string;
  /** Optional icon element rendered above the title. */
  icon?: React.ReactNode;
  /** Optional action element (e.g. a Button) rendered below the message. */
  action?: React.ReactNode;
  /** Outer container style override. */
  style?: ViewStyle;
  /** Test ID for testing. */
  testID?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  message,
  icon,
  action,
  style,
  testID,
}) => (
  <View style={[styles.container, style]} testID={testID}>
    {icon && <View style={styles.iconWrap}>{icon}</View>}
    <Text style={styles.title}>{title}</Text>
    {message ? <Text style={styles.message}>{message}</Text> : null}
    {action && <View style={styles.actionWrap}>{action}</View>}
  </View>
);

EmptyState.displayName = 'EmptyState';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing['2xl'],
    gap: spacing.sm,
  },
  iconWrap: {
    marginBottom: spacing.xs,
  },
  title: {
    ...typography.label.medium,
    fontWeight: '600',
    color: Colors.role.textPrimary,
    textAlign: 'center',
  },
  message: {
    ...typography.label.medium,
    color: Colors.role.textDisabled,
    textAlign: 'center',
  },
  actionWrap: {
    marginTop: spacing.sm,
  },
});
